import React, { useRef, useState } from "react";
import { Folder, FileText, Image, SquarePlay, File } from "lucide-react";

interface Attachment {
  id: number;
  fileName: string;
  fileSize: number;
}

interface FileDropZoneProps {
  onFileAdd: (file: File) => void; // Changed to accept File object
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({ onFileAdd }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        onFileAdd(file); // Pass the actual File object
      });
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        onFileAdd(file); // Pass the actual File object
      });
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept="*/*" // Accept all file types, you can restrict if needed
      />
      <div
        style={{
          ...styles.fileDropArea,
          borderColor: isDragging ? "#E3BD21" : "#ccc",
          backgroundColor: isDragging ? "#fffbf0" : "#fafafa",
        }}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-qa="attachments-dropzone-NEW-FIELD"
      >
        <Folder color="#9CA3AF" />
        <p style={styles.fileDropText}>
          Click here to upload <span style={{ color: "#4B5563" }}>or drop files here</span>
        </p>
      </div>
    </>
  );
};

interface AttachmentItemProps extends Attachment {
  onRemove?: (id: number) => void;
}

export const AttachmentItem: React.FC<AttachmentItemProps> = ({
  id,
  fileName,
  fileSize,
  onRemove,
}) => {
  // Get file extension
  const getFileExtension = (filename: string) => {
    return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
  };

  // Format file size with appropriate unit
  // Format file size with appropriate unit
  const formatFileSize = (size: number) => {

    // Determine if the size is already in MB or bytes
    // If the size is less than 1, it's likely already in MB
    // If the size is large (thousands/millions), it's likely in bytes
    let sizeInBytes;

    if (size < 1024) {
      // If size is small (less than 1024), assume it's already in MB
      sizeInBytes = size * 1024 * 1024;
      // console.log("Assuming size is in MB, converting to bytes:", sizeInBytes);
    } else {
      // If size is large, assume it's already in bytes
      sizeInBytes = size;
      // console.log("Assuming size is in bytes");
    }

    const sizeInMB = sizeInBytes / (1024 * 1024);
    const sizeInKB = sizeInBytes / 1024;

    // console.log("Size in KB:", sizeInKB);
    // console.log("Size in MB:", sizeInMB);

    if (sizeInMB >= 1) {
      // Show in MB if 1 MB or more
      return `${sizeInMB.toFixed(2)}MB`;
    } else {
      // Show in KB for files less than 1 MB
      // Ensure minimum display of 0.01KB for very small files
      const displayKB = Math.max(sizeInKB, 0.01);
      return `${displayKB.toFixed(2)}KB`;
      // return `${sizeInKB.toFixed(2)}KB`;
    }
  };

  const extension = getFileExtension(fileName);

  // Determine icon based on file extension
  const getFileIcon = () => {
    // Document files
    if (["doc", "docx", "txt", "pdf", "rtf", "odt"].includes(extension)) {
      return <FileText color="#E3BD21" />;
    }
    // Video files
    if (["mp4", "avi", "mov", "mkv", "webm", "flv", "wmv"].includes(extension)) {
      return <SquarePlay color="#E3BD21" />;
    }
    // Image files
    if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "ico"].includes(extension)) {
      return <Image color="#E3BD21" />;
    }
    // Default file icon
    return <File color="#E3BD21" />;
  };

  return (
    <div style={styles.attachmentItem}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={styles.attachmentIcon}>{getFileIcon()}</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{fileName}</div>
          <div style={{ fontSize: 12, color: "#666" }}>{formatFileSize(fileSize)}</div>
        </div>
      </div>
      <span
        style={{ fontSize: 20, color: "#999", cursor: "pointer", fontWeight: "bold" }}
        onClick={() => onRemove?.(id)}
        title="Remove file"
      >
        &times;
      </span>
    </div>
  );
};

interface AttachmentListProps {
  attachments: Attachment[];
  onRemove?: (id: number) => void;
}

export const AttachmentList: React.FC<AttachmentListProps> = ({ attachments, onRemove }) => {
  // Don't render anything if there are no attachments
  if (attachments.length === 0) {
    return null;
  }

  return (
    <div data-qa="attachments-list-NEW-FIELD" style={{ marginTop: -20 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10, color: "#333" }}>
        Attachments ({attachments.length})
      </div>
      {attachments.map((file) => (
        <AttachmentItem key={file.id} {...file} onRemove={onRemove} />
      ))}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  fileDropArea: {
    border: "2px dashed #ccc",
    borderRadius: 16,
    padding: 40,
    textAlign: "center",
    marginBottom: 30,
    backgroundColor: "#fafafa",
    cursor: "pointer",
    minHeight: 150,
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  fileDropText: {
    color: "#E3BD21",
    fontSize: 16,
    margin: "10px 0 0 0",
  },
  attachmentItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 15px",
    backgroundColor: "#f9f9f9",
    border: "1px solid #e0e0e0",
    borderRadius: 16,
    marginBottom: 8,
    transition: "all 0.2s ease",
  },
  attachmentIcon: {
    fontSize: 20,
    color: "#666",
    marginRight: 10,
    display: "flex",
    alignItems: "center",
  },
};
